/**
 * Tokens de design extraídos das telas do Claude Design.
 *
 * As telas originais são inline-styled; aqui os valores viram nomes para que
 * uma mudança de marca (o slot "Banco Vantagem" do cabeçalho) seja uma edição
 * só, e não uma varredura por hex espalhado.
 */

export const color = {
  // Superfícies
  bg: '#eef0f2',
  surface: '#ffffff',
  surfaceMuted: '#fafbfc',
  surfaceSunken: '#f4f6f8',
  border: '#e6e8eb',
  borderSoft: '#f1f3f5',
  borderDashed: '#dde1e6',

  // Tinta
  ink: '#293241',
  inkMuted: '#4b5563',
  inkSubtle: '#6b7280',
  inkFaint: '#9aa2af',
  inkGhost: '#c7cdd6',

  // Marca (laranja)
  brand: '#F0862F',
  brandHover: '#E27632',
  brandDeep: '#AB682F',
  brandLight: '#F0A85F',
  brandTint: '#FCF6EC',
  brandTintStrong: '#FCE6CF',
  brandBorder: '#f0dcc0',
  brandInk: '#8a5a1e',

  // Painel escuro (header, nav, orb, rodapé)
  panel: '#293241',
  panelRaised: '#1f2836',
  panelHover: '#2f3a52',
  panelBorder: '#3d4864',
  panelScroll: '#48566b',
  panelText: '#cbd3e6',
  panelTextMuted: '#9fb0cf',
  panelTextFaint: '#8f9db6',
  panelTextSoft: '#a9b6cc',
  panelTextDim: '#7f8fab',

  // Sucesso
  green: '#1e9e63',
  greenInk: '#1c7a44',
  greenTint: '#DDF3E4',
  greenTintSoft: '#f4faf6',
  greenBorder: '#cbe8d6',
  greenBright: '#3DB255',
  greenPanel: '#9fdcbb',

  // Alerta
  amber: '#F0B429',
  amberInk: '#8a5f10',
  amberDeep: '#9a6b12',
  amberTint: '#FBEBC9',

  // Erro / risco
  red: '#c0392b',
  redInk: '#a8332a',
  redDeep: '#8f2c22',
  redTint: '#FBE1DE',
  redBorder: '#f3c9c2',
  redLive: '#ED4F2E',
  redPanel: '#ffb3a7',

  // Informação
  blue: '#2f6fd6',
  blueInk: '#2358a8',
  blueTint: '#E3EDFC',
  blueBorder: '#c8dcf8',

  // Jurídico / contexto
  purple: '#8b5cf6',
  purpleInk: '#6d3fd4',
  purpleDeep: '#5b32bd',
  purpleTint: '#F1EAFE',
  purpleBorder: '#e0d1fb',
} as const

export const font = {
  sans: "Roboto, ui-sans-serif, system-ui, Arial, sans-serif",
  mono: "'Roboto Mono', ui-monospace, monospace",
} as const

export const radius = {
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
  xxl: 14,
  panel: 16,
  pill: 999,
} as const

export const shadow = {
  card: '0 1px 3px rgba(20,25,40,.07)',
  header: '0 2px 10px rgba(20,25,40,.22)',
  nav: '4px 0 10px rgba(0,0,0,.16)',
  band: '0 -8px 26px rgba(20,25,40,.13)',
  footer: '0 -6px 20px rgba(20,25,40,.18)',
  modal: '0 24px 60px rgba(20,25,40,.35)',
  brand: '0 6px 18px rgba(240,134,47,.32)',
} as const

/** Cores das etiquetas que aparecem sob os balões da transcrição. */
export const markStyles = {
  intent: { icon: '⚡', bg: color.blueTint, fg: color.blueInk, bd: color.blueBorder },
  guard: { icon: '🔒', bg: color.redTint, fg: color.redDeep, bd: color.redBorder },
  tool: { icon: '🛠', bg: color.greenTint, fg: color.greenInk, bd: color.greenBorder },
  ctx: { icon: '🧠', bg: color.purpleTint, fg: color.purpleDeep, bd: color.purpleBorder },
  esc: { icon: '↗', bg: color.amberTint, fg: color.amberInk, bd: color.brandBorder },
} as const

export type MarkKind = keyof typeof markStyles

/** Escala do envelope de negociação: 0–55% de desconto mapeados na régua. */
export const DISCOUNT_SCALE = 55
